import { html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { API_TYPE_DASHBOARD, WEB_COMPONENT_FILTERS_BAR } from '../../../webcomponents.constants';
import { styles } from '../view/styles';
import { DisplayMode, LabelWidthValues } from '../../../types/common/types';
import { FilterBarEvents, FilterConfigFrontend } from '../types';
import { ElementClass } from '../../../utils/htmlElement.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { getColumnKeyForFilter } from '../../../grid/utils/filters';
import {
  AllFilterTypeConditions,
  DateConditions,
  IntEnumConditions,
  StringConditions,
} from '@nirvana/shared/dtos/types/shared/filters';
import { AbsoluteTimeRange, TimeRange, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import {
  getComputedTimeRangeFromToday,
  getTimezonedCurrentDateTimestamp,
  jsTimestampToUnixTimestamp,
} from '@nirvana/shared/time';
import { getTimezone } from '../../../utils/global.utils';
import { DateFilterUiConfig, NumberFilterUiConfig } from '../../../types/filters/types';
import { translateFn } from '../../../utils/translator.helper';
import { isEqual } from 'lodash';
import { InputWidthValues } from '../../../ui/gzInput';
import { SliderRange } from '../../../ui/gzSlider/types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { getFilterConfigsByKey, getUTCTimestampsFromDateFilter } from '../../../utils/filters.utils';
import { RootState, store } from '../../../redux/store';
import { connect } from 'pwa-helpers';
import { selectDashboardFilterConfigs, selectDashboardFilterValues } from '../../redux/dashboard.filters.slice';
import { FiltersHelper } from '../helpers/filters.helper';
import { GzSingleSelectComplexWebComponent } from '../../../ui/gzSingleSelectComplex';
import { dashboardConfigurationSelector, filtersPanelActiveStateSelector } from '../../redux/dashboards.ui.slice';
import { DashboardAppState, selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import { Filter, FilterKeys, FilterType } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { GzDashboardFiltersController } from '../controllers/GzDashboardFiltersController';
import { TagsNetworkFilterWebComponent } from '../../../tagsManager/tagsNetworkFilter';
import { RangeSliderTranslateStrings } from '../../../ui/gzRangeSliderInput/types';
import { DateTimeFilterTranslateStrings } from '../../../ui/gzDatetimeFilter/types';
import { longToNumber } from '@nirvana/shared/basic-types/long';
import { selectActiveViewConfiguration } from '../../redux/dashboards.smart.views.slice';
import { DashboardSmartView } from '../../gzDashboardSmartViewsList/types';

/**
 * @summary Displays a filter bar to be used on dashboards
 * @tag gz-dashboard-filters-bar
 */
@customElement(WEB_COMPONENT_FILTERS_BAR)
export class GzDashboardFiltersBarWebComponent extends connect(store)(LitElement) {
  /** @internal */
  private DATE_FILTER_ACTIVE_INTERVAL = 90;
  /** @internal */
  private translations: any;
  /** @internal */
  private commonStrings: any;
  /** @internal */
  private controller: GzDashboardFiltersController;

  /** @internal */
  @state()
  private isFiltersPanelActive: boolean;

  /** @internal */
  @state()
  hasLiveView: boolean;

  /** @internal */
  @query('#company-filter')
  $companyFilter: GzSingleSelectComplexWebComponent;

  /** @internal */
  @query('gz-tags-network-filter')
  tagsFilter: TagsNetworkFilterWebComponent;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  filterConfigs: Array<FilterConfigFrontend>;

  @property({ attribute: false })
  dependencies: any;

  @property({
    type: Array,
    attribute: true,
    reflect: true,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  filters: Array<Filter>;

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  companyDataProvider: string = API_TYPE_DASHBOARD;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  hasReportingPeriodBar = true;

  @property({ attribute: false })
  activeViewConfig: DashboardSmartView;

  @property({ attribute: true })
  public displayMode: DisplayMode;

  static get styles() {
    return styles;
  }

  constructor() {
    super();
    this.getTranslations();
    this.controller = new GzDashboardFiltersController(this);
  }

  stateChanged(rootState: RootState) {
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    this.filters = selectDashboardFilterValues(dashboardsAppState);
    this.filterConfigs = selectDashboardFilterConfigs(dashboardsAppState);
    this.isFiltersPanelActive = filtersPanelActiveStateSelector(dashboardsAppState);
    this.hasLiveView = dashboardConfigurationSelector(dashboardsAppState).liveView.visible;
    this.activeViewConfig = selectActiveViewConfiguration(dashboardsAppState);
  }

  render() {
    return html`
      <div class="wrapper${this.isFiltersPanelActive ? '' : ' hidden'}">
        <div class="filters-container">
          ${this.filters.map(filter => this.renderFilter(filter))} ${this.renderMoreFiltersSelector()}
        </div>
        <div class="filters-action-buttons">${this.renderResetFilters()}</div>
      </div>
      ${this.renderReportingPeriod()}
    `;
  }

  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
  }

  public async resetActiveCompany(): Promise<void> {
    await this.controller.setActiveCompanyFromActiveViewConfig();
  }

  renderFilter(filter: Filter) {
    try {
      const filterKey = getColumnKeyForFilter(filter);
      const filterDetails = getFilterConfigsByKey(this.filterConfigs, filterKey) as FilterConfigFrontend;
      const filterValue = filter[filterDetails.key] as AllFilterTypeConditions;

      if (this.controller.isFilterPredefined(filterDetails)) {
        return this.renderFilterByType(filterDetails, filterValue);
      }

      return html`<gz-removable
        class="${ElementClass.FILTER}"
        @shouldRemoveItem="${this.controller.removeFilter.bind(this.controller, filterKey)}"
      >
        ${this.renderFilterByType(filterDetails, filterValue)}
      </gz-removable>`;
    } catch (e) {
      console.error(e);
      return html`<gz-enum-filter
        class="${ElementClass.FILTER}"
        .labelWidth=${LabelWidthValues.LARGE}
        ?isError=${true}
        .errorMessage=${translateFn('Unable to render filter')}
      />`;
    }
  }

  private renderFilterByType(filterConfig: FilterConfigFrontend, filterValue: AllFilterTypeConditions) {
    switch (filterConfig.type) {
      case FilterType.DATE:
        return this.renderDateTimeFilter(filterConfig, filterValue as DateConditions);
      case FilterType.ENUM:
        return this.renderEnumFilter(filterConfig, filterValue as IntEnumConditions);
      case FilterType.NUMBER:
        return this.renderNumberFilter(filterConfig, filterValue);
      case FilterType.STRING:
        return this.renderStringFilter(filterConfig, filterValue as StringConditions);
      case FilterType.COMPANY:
        return this.renderCompanyFilter(filterConfig);
      case FilterType.SELECT:
        return this.renderSelectFilter(filterConfig, filterValue);
      case FilterType.RANGE:
        return this.renderRangeFilter(filterConfig, filterValue as { range: SliderRange });
      case FilterType.TAGS:
        return this.renderTagsFilter(filterConfig, filterValue as IntEnumConditions);
      default:
        throw new Error(`Unknown filter type ${filterConfig.type}`);
    }
  }

  private renderDateTimeFilter(filterConfig: FilterConfigFrontend, filterValue: DateConditions) {
    const timezone = getTimezone();
    const uiConfig = filterConfig.ui?.config as DateFilterUiConfig;
    const activeRange = uiConfig?.activeRange;
    const enableSelectAllToDate = !!uiConfig?.enableSelectAllToDate;
    const timeRangeOptions = [
      {
        label: this.commonStrings.timeRangeOptions.last24h,
        value: 1,
      },
      {
        label: this.commonStrings.timeRangeOptions.last7days,
        value: 7,
      },
      {
        label: this.commonStrings.timeRangeOptions.last30days,
        value: 30,
      },
    ];
    const selectedTimeRange = filterValue?.timeRange && !isEmpty(filterValue.timeRange) ? filterValue.timeRange : null;

    let min, max;
    const interval: number | null = activeRange !== undefined ? activeRange : this.DATE_FILTER_ACTIVE_INTERVAL;
    if (interval !== null) {
      const activeDateFilterRange: AbsoluteTimeRange = getComputedTimeRangeFromToday(
        interval, // this can be customized
        timezone
      );
      const { fromTimestamp, toTimestamp } = activeDateFilterRange;

      if (fromTimestamp && toTimestamp) {
        min = jsTimestampToUnixTimestamp(fromTimestamp);
      }
    }

    max = getTimezonedCurrentDateTimestamp(timezone);

    return html`
      <gz-datetime-filter
        class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
        .label=${filterConfig.name}
        .enableSelectAllToDate=${enableSelectAllToDate}
        .timezone=${timezone}
        .timeRangeOptions=${timeRangeOptions}
        .selectedTimeRange=${selectedTimeRange}
        .min=${min}
        .max=${max}
        .translateStrings=${{
          confirmButton: this.commonStrings.confirm,
          cancelButton: this.commonStrings.cancel,
          fromLabel: this.commonStrings.from,
          toLabel: this.commonStrings.to,
          customLabel: this.commonStrings.custom,
          allToDate: this.commonStrings.timeRangeOptions.allToDate,
        } as DateTimeFilterTranslateStrings}
        @timeRangeChange=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
        @allToDateSelected=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
      />
    `;
  }

  private renderCompanyFilter(filterConfig: FilterConfigFrontend) {
    if (!FiltersHelper.shouldDisplayCompanyFilter()) {
      return nothing;
    }

    return html`<gz-single-select-complex
      id="company-filter"
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
      isFilter
      cannotDelete
      .apiType=${filterConfig.apiType ? filterConfig.apiType : this.companyDataProvider}
      .label=${filterConfig.name}
      .labelWidth=${LabelWidthValues.LARGE}
      .noSearchResultsString=${this.translations.noOptionsFound}
      .confirmActionButtonString=${this.commonStrings.apply}
      .searchPlaceholderString=${this.commonStrings.search}
      .errorMessage=${this.commonStrings.genericError}
      @selectionChange=${this.controller.handleFilterValueChange.bind(this.controller, FilterKeys.COMPANY)}
    />`;
  }

  private renderEnumFilter(filterConfig: FilterConfigFrontend, filterValue: IntEnumConditions) {
    const canSelectAll =
      filterConfig?.enumFilterCanSelectAll === undefined ? true : filterConfig?.enumFilterCanSelectAll;

    const enumValues = filterConfig.values ?? [];
    if (enumValues.length === 0) {
      throw new Error(`Unexpected enum filter: empty values for column with key ${filterConfig.key}`);
    }

    const selectedValues = {};
    for (const value of filterValue.in) {
      selectedValues[value] = true;
    }

    const filterOptions = enumValues.map(enumValue => ({
      label: enumValue.label,
      value: enumValue.value,
      selected: !!selectedValues[enumValue.value],
    }));

    return html`<gz-enum-filter
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
      .labelWidth=${LabelWidthValues.LARGE}
      .label=${filterConfig.name}
      .noSearchResultsString=${this.translations.noOptionsFound}
      .confirmActionButtonString=${this.commonStrings.confirm}
      .searchPlaceholderString=${this.commonStrings.search}
      .canSelectAll=${canSelectAll}
      .allItemsLabel=${this.commonStrings.all}
      .filterOptions=${filterOptions}
      @selectionChange=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
    />`;
  }

  private renderNumberFilter(filterConfig: FilterConfigFrontend, filterValue) {
    const value = filterValue.hasOwnProperty('eq') ? filterValue.eq : undefined;
    const filterUiConfig = filterConfig.ui?.config as NumberFilterUiConfig;
    const inputWidth = filterUiConfig?.inputWidth ? filterUiConfig.inputWidth : InputWidthValues.MEDIUM;

    return html` <gz-input-number
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
      .inputWidth=${inputWidth}
      .label=${filterConfig.name}
      .placeholder="00"
      .unitPrefix="#"
      ?searchOnAction=${true}
      .value=${value}
      @search=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
    />`;
  }

  private renderStringFilter(filterConfig: FilterConfigFrontend, filterValue: StringConditions) {
    const numConditions = Object.keys(filterValue).length;
    if (numConditions !== 1) {
      throw new Error(`Currently, filter not supported for StringConditions: ${JSON.stringify(filterValue, null, 2)}.`);
    }

    let value;
    if (filterValue.hasOwnProperty('startsWith') && filterValue.startsWith) {
      value = filterValue.startsWith;
    } else if (filterValue.hasOwnProperty('regex') && filterValue.regex) {
      value = filterValue.regex.pattern;
    } else if (filterValue.hasOwnProperty('equals') && filterValue.equals) {
      value = filterValue.equals;
    } else if (filterValue.hasOwnProperty('contains') && filterValue.contains === '') {
      // Filter matching all records.
      value = filterValue.contains;
    } else {
      throw new Error(`Currently, filter not supported for StringConditions: ${JSON.stringify(filterValue, null, 2)}.`);
    }

    return html`<gz-input-search
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
      .inputWidth=${InputWidthValues.LARGE}
      .label=${filterConfig.name}
      .placeholder=${this.translations.stringFilterPlaceholder}
      ?searchOnAction=${true}
      .value=${value}
      @search=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
    />`;
  }

  private renderSelectFilter(filterConfig: FilterConfigFrontend, filterValue) {
    const options = filterConfig.values?.map(({ label: title, value }) => ({ value, title }));

    return html` <gz-dropdown
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''} dropdown-filter"
      .label=${filterConfig.name}
      .labelWidth=${LabelWidthValues.LARGE}
      ?canDelete=${false}
      value=${(typeof filterValue?.equals  === 'object') ? longToNumber(filterValue?.equals) : filterValue?.equals}
      .options=${options}
      @optionSelected=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
    />`;
  }

  private renderRangeFilter(filterConfig: FilterConfigFrontend, filterValue: { range: SliderRange }) {
    return html` <gz-range-slider-input
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
      .label=${filterConfig.name}
      .placeholder="${this.translations.chooseLabel}..."
      ?autoAdjustMenuPosition=${true}
      .translateStrings=${{
        confirmButton: this.translations.confirmButton,
        cancelButton: this.translations.cancelButton,
        fromLabel: this.translations.fromLabel,
        toLabel: this.translations.toLabel,
      } as RangeSliderTranslateStrings}
      ?confirmBtnDisabled=${true}
      .selectedSliderRange=${filterValue.range}
      @rangeSliderConfirmed=${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}
    />`;
  }

  private renderTagsFilter(filterConfig: FilterConfigFrontend, filterValue: IntEnumConditions) {
    const companyId = this.controller.getCompanyIdFromFilters();
    const shouldFetchData = !!companyId.length;
    return html`<gz-tags-network-filter
      class="${this.controller.isFilterPredefined(filterConfig) ? ElementClass.FILTER : ''}"
      label="${this.translations.tags}"
      labelWidth="${LabelWidthValues.LARGE}"
      data-companyIds="${companyId.length ? JSON.stringify([companyId]) : JSON.stringify([])}"
      should-fetch-data="${shouldFetchData}"
      @selectionChange="${this.controller.handleFilterValueChange.bind(this.controller, filterConfig.key)}"
    >
    </gz-tags-network-filter>`;
  }

  private renderMoreFiltersSelector() {
    const optionalFilters = this.filterConfigs.filter(
      filterConfig => !this.controller.isFilterPredefined(filterConfig)
    );

    // There are no filters to display in the 'More' dropdown, so we don't need to display it
    if (isEmpty(optionalFilters)) {
      return nothing;
    }

    // Foreach not predefined filter, add them to More list with selected:false
    const activeFilterKeys = this.filters.map(filter => Object.keys(filter)[0]);
    const filterOptions = optionalFilters.map(filterConfig => ({
      label: filterConfig.name,
      value: filterConfig.key,
      selected: activeFilterKeys.includes(filterConfig.key),
    }));

    return html`<gz-enum-filter
      class="filter"
      .labelWidth=${LabelWidthValues.AUTO}
      .label=${this.commonStrings.more}
      .noSearchResultsString=${this.translations.noOptionsFound}
      .confirmActionButtonString=${this.commonStrings.apply}
      .searchPlaceholderString=${this.commonStrings.search}
      .allItemsLabel=${this.commonStrings.all}
      ?autoAdjustMenuPosition=${true}
      .filterOptions=${filterOptions}
      ?canSelectAll=${filterOptions.length > 1}
      @selectionChange=${this.controller.onMoreFiltersUpdated.bind(this.controller)}
    />`;
  }

  private renderResetFilters() {
    return html`
      <gz-button
        type="quiet"
        color="primary"
        buttonHeight="small"
        buttonFontSize="small"
        lowercase
        id="clearFilters"
        @click="${this.resetFilters}"
      >
        ${this.commonStrings.clear}
      </gz-button>
    `;
  }

  private async resetFilters() {
    await this.controller.resetFilters();

    this.dispatchEvent(
      new CustomEvent(FilterBarEvents.FILTERS_CHANGE, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderReportingPeriod() {
    if (!this.hasReportingPeriodBar) {
      return nothing;
    }

    const timeRange = this.getTimeRange();
    if (!timeRange) {
      // We are unable to use a reporting period bar
      return nothing;
    }

    const timezone = getTimezone();
    const formattedTimeRange = getUTCTimestampsFromDateFilter(timeRange, timezone);

    if (!formattedTimeRange) {
      return nothing;
    }

    return html`<gz-reporting-period
      class="reporting-period"
      .fromTimestamp="${ifDefined(formattedTimeRange.fromTimestamp)}"
      .toTimestamp=${ifDefined(formattedTimeRange.toTimestamp)}
      .timezone=${timezone}
      .title=${`${translateFn('Reporting period')}:`}
    />`;
  }

  private getTimeRange() : TimeRange | undefined {
    let timeRange = this.getTimeRangeFromDateFilter();
    if (timeRange) {
      return timeRange;
    }

    timeRange = this.getTimeRangeFromSelectFilter();
    if (timeRange) {
      return timeRange;
    }

    return;
  }

  private getTimeRangeFromDateFilter() : TimeRange | undefined {
    const dateFilterConfig = this.filterConfigs.find(filterConfig => filterConfig.type === FilterType.DATE);
    const dateFilter = this.filters.find(filter => getColumnKeyForFilter(filter) === dateFilterConfig?.key);

    if (!dateFilterConfig || !dateFilter) {
      return;
    }

    return (dateFilter[dateFilterConfig.key] as DateConditions).timeRange;
  }

  private getTimeRangeFromSelectFilter() : TimeRange | undefined {
    const selectFilterConfig = this.filterConfigs.find(filterConfig =>
      filterConfig.type === FilterType.SELECT && filterConfig.key === FilterKeys.LAST_SEEN);
    const selectFilter = this.filters.find(filter => getColumnKeyForFilter(filter) === selectFilterConfig?.key);

    if (!selectFilterConfig || !selectFilter) {
      return;
    }

    // the select filter value can be either a string or a number (long),
    // so we assert it as a StringConditions type and convert it later to a number
    const lastNumDays = (selectFilter[selectFilterConfig.key] as StringConditions)?.equals;
    if (!lastNumDays) {
      return;
    }

    return {
      kind: TimeRangeKind.RELATIVE_TIME_RANGE,
      relative: {
        lastNumDays: (typeof lastNumDays === 'object') ? longToNumber(lastNumDays) : Number(lastNumDays),
      }
    };
  }

  getTranslations() {
    this.translations = {
      noOptionsFound: translateFn('No options found'),
      stringFilterPlaceholder: translateFn('type here'),
      chooseLabel: translateFn('Choose'),
      confirmButton: translateFn('Confirm'),
      cancelButton: translateFn('Cancel'),
      fromLabel: translateFn('From'),
      toLabel: translateFn('To'),
      tags: translateFn('Tags'),
    };

    this.commonStrings = {
      more: translateFn('More'),
      apply: translateFn('Apply'),
      search: translateFn('Search'),
      all: translateFn('All'),
      clear: translateFn('Reset filters'),
      custom: translateFn('Custom'),
      from: translateFn('From'),
      to: translateFn('To'),
      cancel: translateFn('Cancel'),
      confirm: translateFn('Confirm'),
      timeRangeOptions: {
        last24h: translateFn('Last 24 hours'),
        last7days: translateFn('Last 7 days'),
        last30days: translateFn('Last 30 days'),
        allToDate: translateFn('All (to date)'),
      },
    };
  }
}
